import gql from 'graphql-tag';

import type { AudienceOptionType } from '@/api/audience';
import { getVariablesFromTableParamsNew } from '@/api/util/getVariablesFromTableParams';
import type { SolQueryParamsNew } from '@/components/InstantSearch';
import { useSolQuery } from '@/hooks/useSolQuery';

import type {
  OfflineActivityGQLResponse,
  OfflineTimelineGQLResponse,
  OpportunityOfflineActivityGQLResponse,
} from './offlineEventsType';

export const useOfflineActivity = (
  tableParams?: SolQueryParamsNew,
  startDate?: string,
  endDate?: string,
  audienceId?: string,
) => {
  const canExecuteQuery = tableParams && startDate?.length && endDate?.length;
  const { data, isLoading, error } = useSolQuery<OfflineActivityGQLResponse>({
    query:
      !!canExecuteQuery &&
      gql`
        query OfflineActivity(
          $page: Pagination!
          $sort: [SortParamInput]!
          $endDate: DateTime!
          $startDate: DateTime!
          $filter: [FilterParamInput]!
          $audienceId: String
          $opportunityId: String
        ) {
          visualization {
            offlineActivity(
              page: $page
              sort: $sort
              endDate: $endDate
              startDate: $startDate
              filter: $filter
              audienceId: $audienceId
              opportunityId: $opportunityId
            ) {
              campaignType {
                id
                name
                vendor {
                  channel {
                    id
                    name
                    color
                  }
                  id
                  name
                  color
                }
              }
              closedWonFirstTouchAttribution
              closedWonInfluenced
              closedWonLinearAttribution
              countClosedwonInfluenced
              countPipelineInfluenced
              customImpactScore
              engagedCompanies
              opportunityInfluencedSumClosedWonCustom
              opportunityInfluencedSumPipelineCustom
              otherViewThroughVisits
              otherVisits
              pipelineFirstTouchAttribution
              pipelineInfluenced
              pipelineLastTouchAttribution
              pipelineLinearAttribution
              spend
              targetViewThroughVisits
              targetVisits
              viewThroughPercent
              viewThroughVisits
              visits
            }
          }
        }
      `,
    variables: {
      ...getVariablesFromTableParamsNew(tableParams),
      page: {
        offset: 0,
        limit: 100,
      },
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      audienceId: audienceId,
    },
  });

  return {
    offlineActivity: data?.visualization.offlineActivity,
    isLoading,
    error,
  };
};

export const useOpportunityOfflineActivity = (
  tableParams?: SolQueryParamsNew,
  startDate?: string,
  endDate?: string,
  audience?: AudienceOptionType,
  opportunityId?: string,
) => {
  const canExecuteQuery = tableParams && startDate?.length && endDate?.length;
  const { data, isLoading, error } = useSolQuery<OpportunityOfflineActivityGQLResponse>(
    {
      query:
        !!canExecuteQuery &&
        gql`
          query OpportunityOfflineActivity(
            $page: Pagination!
            $sort: [SortParamInput]!
            $endDate: DateTime!
            $startDate: DateTime!
            $filter: [FilterParamInput]!
            $audienceId: String
            $opportunityId: String
          ) {
            visualization {
              opportunityOfflineActivity(
                page: $page
                sort: $sort
                endDate: $endDate
                startDate: $startDate
                filter: $filter
                audienceId: $audienceId
                opportunityId: $opportunityId
              ) {
                audienceOfflineEvents
                campaignType {
                  id
                  name
                  vendor {
                    channel {
                      id
                      name
                      color
                    }
                    id
                    name
                    color
                  }
                }
                closedWonFirstTouchAttribution
                closedWonInfluenced
                closedWonLinearAttribution
                opportunityInfluencedSumClosedWonCustom
                opportunityInfluencedSumPipelineCustom
                pipelineFirstTouchAttribution
                pipelineInfluenced
                pipelineLastTouchAttribution
                pipelineLinearAttribution
              }
            }
          }
        `,
      variables: {
        ...getVariablesFromTableParamsNew(tableParams),
        page: {
          offset: 0,
          limit: 100,
        },
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
        audienceId: audience?.id,
        opportunityId: opportunityId,
      },
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    offlineActivity: data?.visualization.opportunityOfflineActivity,
    isLoading,
    error,
  };
};

export const useOfflineTimeline = (
  startDate?: string,
  endDate?: string,
  { opportunityId, companyId }: { companyId?: string; opportunityId?: string } = {},
) => {
  const canExecuteQuery = !!(opportunityId || companyId) && !!startDate && !!endDate;
  const { data, isLoading, error } = useSolQuery<OfflineTimelineGQLResponse>({
    query:
      canExecuteQuery &&
      gql`
        query OfflineTimeline2(
          $startDate: DateTime!
          $endDate: DateTime!
          $opportunityIds: [String!]
          $companyIds: [String!]
        ) {
          visualization {
            offlineTimeline2(
              startDate: $startDate
              endDate: $endDate
              opportunityIds: $opportunityIds
              companyIds: $companyIds
            ) {
              campaignType {
                id
                name
                vendor {
                  channel {
                    name
                  }
                  color
                  id
                  name
                }
              }
              data {
                events {
                  eventId
                  eventUrl
                  eventDate
                }
                sundayOfWeek
              }
            }
          }
        }
      `,
    variables: {
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      opportunityIds: opportunityId ? [opportunityId] : undefined,
      companyIds: companyId ? [companyId] : undefined,
    },
  });

  return {
    offlineTimeline: data?.visualization.offlineTimeline2,
    isLoading,
    error,
  };
};
